<template>
  <transition name="connection-pop">
    <div
      v-if="visible"
      class="fixed bottom-6 right-6 z-[9999] w-[370px] overflow-hidden rounded-[26px] border border-white/50 bg-white/90 shadow-[0_24px_70px_rgba(6,63,47,0.28)] backdrop-blur-xl bodyTemp"
    >
      <!-- Top Accent -->
      <div
        class="h-1.5 w-full"
        :class="isOnline && backendOk
          ? 'bg-gradient-to-r from-[#198760] via-[#147452] to-[#0f5f42]'
          : 'bg-gradient-to-r from-red-400 via-red-500 to-[#147452]'"
      ></div>

      <!-- Header -->
      <div
        class="relative overflow-hidden px-5 py-5"
        :class="isOnline && backendOk
          ? 'bg-gradient-to-br from-green-50 via-white to-green-50'
          : 'bg-gradient-to-br from-red-50 via-white to-green-50'"
      >
        <!-- Soft Decoration -->
        <div class="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#147452]/10"></div>
        <div class="absolute -bottom-12 -left-10 h-28 w-28 rounded-full bg-red-400/10"></div>

        <div class="relative flex items-start gap-4">
          <div
            class="flex h-14 w-14 flex-none items-center justify-center rounded-2xl shadow-sm"
            :class="isOnline && backendOk
              ? 'bg-[#147452] text-white'
              : 'bg-red-100 text-red-500 ring-1 ring-red-200'"
          >
            <svg
              v-if="isOnline && backendOk"
              class="h-7 w-7"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>

            <svg
              v-else
              class="h-7 w-7"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v5" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 17h.01" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>

          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <h3 class="text-[15px] font-extrabold text-slate-800">
                {{ title }}
              </h3>

              <span
                class="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                :class="isOnline && backendOk
                  ? 'bg-green-100 text-[#147452]'
                  : 'bg-red-100 text-red-600'"
              >
                {{ isOnline && backendOk ? 'Online' : 'Issue' }}
              </span>
            </div>

            <p class="mt-1 text-xs leading-relaxed text-slate-500">
              {{ subtitle }}
            </p>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="space-y-3 px-5 py-5">
        <!-- Internet -->
        <div class="group flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3.5 shadow-sm">
          <div class="flex items-center gap-3">
            <div
              class="flex h-9 w-9 items-center justify-center rounded-xl"
              :class="isOnline ? 'bg-green-100 text-[#147452]' : 'bg-red-100 text-red-500'"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M5.636 13.929a9 9 0 0112.728 0" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.515 11.808a12 12 0 0116.97 0" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 20h.01" />
              </svg>
            </div>

            <div>
              <p class="text-sm font-bold text-slate-700">
                Internet
              </p>
              <p class="text-[11px] text-slate-400">
                Network availability
              </p>
            </div>
          </div>

          <span
            class="rounded-full px-3 py-1 text-[11px] font-extrabold"
            :class="isOnline ? 'bg-green-100 text-[#147452]' : 'bg-red-100 text-red-600'"
          >
            {{ isOnline ? 'Connected' : 'Disconnected' }}
          </span>
        </div>

        <!-- Backend -->
        <div class="group flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3.5 shadow-sm">
          <div class="flex items-center gap-3">
            <div
              class="flex h-9 w-9 items-center justify-center rounded-xl"
              :class="backendOk ? 'bg-green-100 text-[#147452]' : 'bg-red-100 text-red-500'"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 7a8 3 0 1016 0A8 3 0 004 7z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 7v10a8 3 0 0016 0V7" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 12a8 3 0 0016 0" />
              </svg>
            </div>

            <div>
              <p class="text-sm font-bold text-slate-700">
                Backend Server
              </p>
              <p class="text-[11px] text-slate-400">
                API service status
              </p>
            </div>
          </div>

          <span
            class="rounded-full px-3 py-1 text-[11px] font-extrabold"
            :class="backendOk ? 'bg-green-100 text-[#147452]' : 'bg-red-100 text-red-600'"
          >
            {{ backendOk ? 'Running' : 'Down' }}
          </span>
        </div>

        <!-- Button -->
        <!-- <button
          @click="checkBackend"
          class="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#198760] via-[#147452] to-[#126647] px-4 py-3 text-sm font-extrabold text-white shadow-lg shadow-green-900/20 transition duration-300 hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98]"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v6h6" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M20 20v-6h-6" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M20 9A8 8 0 006.34 4.34L4 6.5" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 15a8 8 0 0013.66 4.66L20 17.5" />
          </svg>
          Check Again
        </button> -->
      </div>
    </div>
  </transition>
</template>

<script>
import axios from "axios";

export default {
  name: "ConnectionStatus",

  data() {
    return {
        isOnline: navigator.onLine,
        backendOk: true,
        visible: false,
        interval: null,

        hadConnectionIssue: false,
        recoveryTimer: null,
    };
    },

  computed: {
    title() {
      if (!this.isOnline) return "No Internet Connection";
      if (!this.backendOk) return "Backend Server is Down";
      return "Connection Restored";
    },

    subtitle() {
      if (!this.isOnline) return "Please check your network connection.";
      if (!this.backendOk) return "The system cannot reach the server.";
      return "Everything is working properly.";
    },
  },

  mounted() {
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    this.checkBackend();

    this.interval = setInterval(() => {
      this.checkBackend();
    }, 10000);
  },

  beforeUnmount() {
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);

    clearInterval(this.interval);
    clearTimeout(this.recoveryTimer);
    },

  methods: {
    handleOnline() {
        this.isOnline = true;
        this.checkBackend();
    },

    handleOffline() {
        this.isOnline = false;
        this.backendOk = false;
        this.hadConnectionIssue = true;
        this.visible = true;
    },

    showRecoveredBriefly() {
        if (!this.hadConnectionIssue) {
        this.visible = false;
        return;
        }

        this.visible = true;

        clearTimeout(this.recoveryTimer);

        this.recoveryTimer = setTimeout(() => {
        this.visible = false;
        this.hadConnectionIssue = false;
        }, 2500);
    },

    async checkBackend() {
        this.isOnline = navigator.onLine;

        if (!this.isOnline) {
        this.backendOk = false;
        this.hadConnectionIssue = true;
        this.visible = true;
        return;
        }

        try {
        await axios.get(process.env.VUE_APP_BASE_URL + "/ping/ping", {
            timeout: 5000,
        });

        this.backendOk = true;

        // ✅ show green popup briefly only after recovery
        this.showRecoveredBriefly();

        } catch (error) {
        this.backendOk = false;
        this.hadConnectionIssue = true;
        this.visible = true;
        }
    },
    },
};
</script>

<style scoped>

@import url("https://fonts.googleapis.com/css2?family=Roboto&display=swap");

.bodyTemp {
  font-family: "Roboto";
}

.connection-pop-enter-active,
.connection-pop-leave-active {
  transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.connection-pop-enter-from,
.connection-pop-leave-to {
  opacity: 0;
  transform: translateY(24px) scale(0.94);
}

.connection-pop-enter-to,
.connection-pop-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

</style>